import { Alert, Card, Empty, Skeleton, Table, Tooltip, Typography } from 'antd'
import { useMemo } from 'react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import VSpace from '../../common/VSpace'
import { centsToDollarsCompact } from '../../displayFormatters'
import { usePaymentsRetention } from './paymentsRetentionQueries'
import {
  PaymentsRetentionCohort,
  PaymentsRetentionTrendMonth,
  RevertedTenant,
  buildTrendPoints,
  cohortCellShade,
  cohortColumnCount,
  describeCohortMonth,
  describeInvoicingSystem,
  findCohortMonth,
  formatRetentionDate,
  formatRetentionMonth,
  formatRetentionPct,
} from './paymentsRetentionModel'

const { Title, Text } = Typography

const ON_SYNKEDUP_COLOR = '#2a78d6'
const VOLUME_SHARE_COLOR = '#eb6834'

type TrendProps = {
  trend: PaymentsRetentionTrendMonth[]
}

const RetentionTrendChart = ({ trend }: TrendProps) => {
  const points = useMemo(() => buildTrendPoints(trend), [trend])
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={points} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" minTickGap={16} />
        <YAxis domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} width={48} />
        <ChartTooltip formatter={(value: number, name: string) => [formatRetentionPct(value ?? null), name]} />
        <Legend />
        <Line
          type="monotone"
          dataKey="onSynkedUpPct"
          name="% of invoicing switchers on SynkedUP"
          stroke={ON_SYNKEDUP_COLOR}
          strokeWidth={2}
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
        />
        <Line
          type="monotone"
          dataKey="synkedUpVolumePct"
          name="SynkedUP share of invoice dollars"
          stroke={VOLUME_SHARE_COLOR}
          strokeWidth={2}
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

const RetentionTrendTable = ({ trend }: TrendProps) => (
  <Table
    dataSource={trend}
    rowKey="month"
    pagination={false}
    size="small"
    scroll={{ x: true }}
    columns={[
      { title: 'Month', dataIndex: 'month', render: (v: string) => formatRetentionMonth(v) },
      { title: 'Switched', dataIndex: 'switchedTenants' },
      { title: 'Invoicing', dataIndex: 'invoicingTenants' },
      { title: 'On SynkedUP', dataIndex: 'onSynkedUpTenants' },
      { title: 'Reverted', dataIndex: 'revertedTenants' },
      { title: 'Idle', dataIndex: 'idleTenants' },
      { title: '% On SynkedUP', dataIndex: 'onSynkedUpPct', render: (v: number | null) => formatRetentionPct(v) },
      { title: 'SynkedUP $', dataIndex: 'synkedUpVolume', render: (v: number) => centsToDollarsCompact(v) },
      { title: 'Total $', dataIndex: 'totalVolume', render: (v: number) => centsToDollarsCompact(v) },
      { title: 'SynkedUP $ share', dataIndex: 'synkedUpVolumePct', render: (v: number | null) => formatRetentionPct(v) },
    ]}
  />
)

type CohortProps = {
  cohorts: PaymentsRetentionCohort[]
}

const CohortTable = ({ cohorts }: CohortProps) => {
  const columnCount = cohortColumnCount(cohorts)
  const monthColumns = Array.from({ length: columnCount }, (_, monthsSinceSwitch) => ({
    title: `M${monthsSinceSwitch}`,
    key: `m${monthsSinceSwitch}`,
    align: 'center' as const,
    render: (_: unknown, cohort: PaymentsRetentionCohort) => {
      const month = findCohortMonth(cohort, monthsSinceSwitch)
      if (!month) {
        return null
      }
      return (
        <Tooltip title={describeCohortMonth(month)}>
          <span>{formatRetentionPct(month.onSynkedUpPct)}</span>
        </Tooltip>
      )
    },
    onCell: (cohort: PaymentsRetentionCohort) => {
      const month = findCohortMonth(cohort, monthsSinceSwitch)
      const background = month ? cohortCellShade(month.onSynkedUpPct) : undefined
      return background ? { style: { background } } : {}
    },
  }))

  return (
    <Table
      dataSource={cohorts}
      rowKey="switchMonth"
      pagination={false}
      size="small"
      bordered
      scroll={{ x: true }}
      columns={[
        { title: 'Switch month', dataIndex: 'switchMonth', fixed: 'left', render: (v: string) => formatRetentionMonth(v) },
        { title: 'Tenants', dataIndex: 'tenantCount', fixed: 'left' },
        ...monthColumns,
      ]}
    />
  )
}

type RevertedProps = {
  tenants: RevertedTenant[]
}

const RevertedTenantsTable = ({ tenants }: RevertedProps) => (
  <Table
    dataSource={tenants}
    rowKey="tenantId"
    pagination={false}
    size="small"
    locale={{ emptyText: 'No switchers have reverted to QuickBooks.' }}
    columns={[
      { title: 'Tenant', dataIndex: 'tenantName' },
      { title: 'Tenant ID', dataIndex: 'tenantId' },
      { title: 'Switched', dataIndex: 'switchedAt', render: (v: string) => formatRetentionDate(v) },
      { title: 'Last SynkedUP invoice', dataIndex: 'lastSynkedUpInvoiceAt', render: (v: string) => formatRetentionDate(v) },
      { title: 'Last QuickBooks invoice', dataIndex: 'lastQuickBooksInvoiceAt', render: (v: string) => formatRetentionDate(v) },
      { title: 'Current invoicing setting', dataIndex: 'currentInvoicingSystem', render: (v: string | null) => describeInvoicingSystem(v) },
    ]}
  />
)

const PaymentsRetention = () => {
  const { data, isLoading, isError } = usePaymentsRetention()

  if (isError) {
    return <Alert type="error" message="Could not load the payments retention report." />
  }
  if (isLoading || !data) {
    return <Skeleton active />
  }

  return (
    <VSpace>
      <Title level={3}>Payments Retention</Title>
      <Text type="secondary">
        {data.switcherCount} tenants switched from QuickBooks to SynkedUP invoicing. A month is On SynkedUP
        when every invoice was SynkedUP, Reverted when any was QuickBooks, and Idle when there were none;
        idle tenants are left out of the percentages. Months are UTC. Computed live
        as of {formatRetentionDate(data.generatedAt)}.
      </Text>

      <Card size="small" title="Last 12 months">
        {data.switcherCount === 0 ? (
          <Empty description="No tenants have switched yet" />
        ) : (
          <VSpace>
            <RetentionTrendChart trend={data.trend} />
            <RetentionTrendTable trend={data.trend} />
          </VSpace>
        )}
      </Card>

      <Card size="small" title="Cohorts by switch month">
        <VSpace>
          <Text type="secondary">
            Each cell is % On SynkedUP for that cohort, M0 being the switch month. Hover a cell for its
            On SynkedUP, Reverted and Idle counts.
          </Text>
          <CohortTable cohorts={data.cohorts} />
        </VSpace>
      </Card>

      <Card size="small" title="Currently reverted tenants">
        <VSpace>
          <Text type="secondary">Switchers whose most recent invoice was a QuickBooks invoice.</Text>
          <RevertedTenantsTable tenants={data.revertedTenants} />
        </VSpace>
      </Card>
    </VSpace>
  )
}

export default PaymentsRetention
